/**
 * Operating-system notifications, for notices that arrive while the panel is
 * not the window in front.
 *
 * The browser only asks for permission in response to a click, so asking is a
 * button in the inbox menu rather than something the panel does on load — and
 * a prompt nobody asked for is the one people deny on reflex, after which the
 * browser never shows it again.
 *
 * Every accessor is guarded: `Notification` does not exist in some browsers,
 * in an insecure context, or inside some embedded webviews, and a missing API
 * must mean "no desktop alerts", never a crash.
 */

import { isPushActive } from "./push";

export type DesktopAlertPermission = NotificationPermission | "unsupported";

export function desktopAlertPermission(): DesktopAlertPermission {
  try {
    return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
  } catch {
    return "unsupported";
  }
}

export async function requestDesktopAlerts(): Promise<DesktopAlertPermission> {
  if (desktopAlertPermission() === "unsupported") return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return desktopAlertPermission();
  }
}

/**
 * Shows one, if permitted — whether or not the panel is in view, so every
 * notice also lands in the operating system's notification centre, where it
 * can be found later after the in-app toast has gone.
 *
 * `tag` is the notice id, so a second tab raising the same notice replaces the
 * first alert rather than stacking a duplicate.
 */
export function showDesktopAlert({
  tag,
  title,
  body,
  onClick,
}: {
  tag: string;
  title: string;
  body: string;
  onClick?: () => void;
}): void {
  if (desktopAlertPermission() !== "granted") return;
  // The service worker already shows this one from the push; a second copy
  // from the page would put every notice in the notification centre twice.
  if (isPushActive()) return;

  try {
    const alert = new Notification(title, {
      body,
      tag,
      icon: `${import.meta.env.BASE_URL}media/images/favicon.png`,
    });
    alert.onclick = () => {
      window.focus();
      onClick?.();
      alert.close();
    };
  } catch {
    // Some platforms only allow notifications from a service worker. The
    // in-app toast and the badge still carry the notice.
  }
}
